import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, DatabaseBackup, HardDrive, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { DataTable, KpiCard, num, type DataColumn } from "@/components/admin/analytics/AnalyticsKit";
import { EmptyState } from "@/components/admin/EmptyState";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  createSystemBackup,
  deleteSystemBackup,
  downloadBackupPayload,
  listSystemBackups,
  restoreSystemBackup,
} from "@/lib/admin/system/system.functions";
import type { SystemBackup } from "@/lib/admin/system/system.types";

export const Route = createFileRoute("/_admin/admin/backup-restore")({
  head: () => ({
    meta: [
      { title: "Backup & Restore — Admin" },
      {
        name: "description",
        content: "Create, download, restore and manage system backups before any production change.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BackupRestorePage,
});

function mb(bytes: number) {
  if (!bytes) return "—";
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function BackupRestorePage() {
  const qc = useQueryClient();
  const list = useServerFn(listSystemBackups);
  const create = useServerFn(createSystemBackup);
  const restore = useServerFn(restoreSystemBackup);
  const remove = useServerFn(deleteSystemBackup);
  const download = useServerFn(downloadBackupPayload);

  const [label, setLabel] = useState("");
  const [comments, setComments] = useState("");
  const [confirmRestore, setConfirmRestore] = useState<SystemBackup | null>(null);

  const { data: backups = [], isLoading } = useQuery({
    queryKey: ["admin", "system-backups"],
    queryFn: () => list(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "system-backups"] });

  const createMutation = useMutation({
    mutationFn: () =>
      create({
        data: {
          label: label.trim() || `Backup ${new Date().toLocaleString("en-IN")}`,
          comments: comments.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setLabel("");
      setComments("");
      toast.success("Backup created.");
      void invalidate();
    },
    onError: () => toast.error("Backup failed."),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restore({ data: { id } }),
    onSuccess: () => {
      toast.success("Backup restored. A safety backup was taken first.");
      void invalidate();
    },
    onError: () => toast.error("Restore failed."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Backup deleted.");
      void invalidate();
    },
    onError: () => toast.error("Backup could not be deleted."),
  });

  const handleDownload = async (row: SystemBackup) => {
    const res = await download({ data: { id: row.id } });
    if (!res) {
      toast.error("Backup payload not found.");
      return;
    }
    const blob = new Blob([JSON.stringify(res.payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${row.label.replace(/[^\w.-]+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: Array<DataColumn<SystemBackup>> = [
    {
      key: "created_at",
      header: "Backup date",
      render: (r) => new Date(r.created_at).toLocaleString("en-IN"),
      sortValue: (r) => r.created_at,
      exportValue: (r) => r.created_at,
    },
    { key: "label", header: "Label", render: (r) => r.label, exportValue: (r) => r.label },
    {
      key: "created_by",
      header: "Created by",
      render: (r) => r.created_by_email ?? "—",
      exportValue: (r) => r.created_by_email ?? "",
    },
    {
      key: "db",
      header: "Database size",
      align: "right",
      render: (r) => mb(r.db_size_bytes),
      sortValue: (r) => r.db_size_bytes,
      exportValue: (r) => r.db_size_bytes,
    },
    {
      key: "storage",
      header: "Storage size",
      align: "right",
      render: (r) => mb(r.storage_size_bytes),
      sortValue: (r) => r.storage_size_bytes,
      exportValue: (r) => r.storage_size_bytes,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge variant="secondary">{r.status}</Badge>,
      exportValue: (r) => r.status,
    },
    {
      key: "comments",
      header: "Comments",
      render: (r) => r.comments ?? "—",
      exportValue: (r) => r.comments ?? "",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => void handleDownload(r)} className="gap-1">
            <Download className="h-3.5 w-3.5" aria-hidden /> Download
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmRestore(r)}
            className="gap-1"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Restore
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => deleteMutation.mutate(r.id)}
            className="gap-1 text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      ),
      exportValue: () => "",
    },
  ];

  const latest = backups[0];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Backup & Restore"
        description="Snapshot the client's configuration before any production change, and roll back whenever needed."
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard label="Backups" value={num(backups.length)} tone="info" icon={DatabaseBackup} loading={isLoading} />
        <KpiCard
          label="Latest backup"
          value={latest ? new Date(latest.created_at).toLocaleDateString("en-IN") : "None"}
          tone="teal"
          icon={HardDrive}
          hint={latest?.label}
          loading={isLoading}
        />
        <KpiCard
          label="Database size"
          value={latest ? mb(latest.db_size_bytes) : "—"}
          tone="purple"
          icon={HardDrive}
          loading={isLoading}
        />
      </div>

      <AdminSection
        number={1}
        icon={DatabaseBackup}
        title="Create Backup"
        description="Captures every system and website setting plus current record counts."
      >
        <div className="grid max-w-2xl gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Before go-live cleanup"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="comments">Comments</Label>
            <Textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={2}
              placeholder="Optional note for the implementation record"
            />
          </div>
          <div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              className="gap-2"
            >
              <DatabaseBackup className="h-4 w-4" aria-hidden />
              {createMutation.isPending ? "Creating…" : "Create Backup"}
            </Button>
          </div>
        </div>
      </AdminSection>

      <AdminSection
        number={2}
        icon={HardDrive}
        title="Backup History"
        description="Download, restore or delete an earlier snapshot. Restoring always takes a safety backup first."
      >
        {!isLoading && backups.length === 0 ? (
          <EmptyState
            icon={DatabaseBackup}
            title="No Backups Yet"
            description="Create your first snapshot before making production changes."
          />
        ) : (
          <DataTable
            rows={backups}
            columns={columns}
            loading={isLoading}
            exportName="system-backups"
            minWidth={1100}
            searchOn={(r) => `${r.label} ${r.created_by_email ?? ""} ${r.comments ?? ""}`}
          />
        )}
      </AdminSection>

      <AlertDialog open={confirmRestore !== null} onOpenChange={(v) => !v && setConfirmRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              This restores "{confirmRestore?.label}", overwriting current settings. A safety backup is taken first, but this action cannot be undone otherwise.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmRestore) { restoreMutation.mutate(confirmRestore.id); setConfirmRestore(null); } }}
            >
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
